import React from 'react';
import { CheckCircle, XCircle, Clock, ExternalLink, ClipboardList } from 'lucide-react';
import { LeadClaim } from '@/services/claimsService';
import { format } from 'date-fns';

export type ClaimsMode = 'pending' | 'all' | 'mine';

const statusConfig = {
  pending:  { label: 'Pending',  color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800',  icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-800',      icon: XCircle },
};

export const safeDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yy, hh:mm a'); } catch { return d; }
};

interface Props {
  claims: LeadClaim[];
  mode: ClaimsMode;
  isSuperAdmin: boolean;
  actionLoading?: string | null;
  adminNotes?: Record<string, string>;
  onApprove?: (uuid: string) => void;
  onDecline?: (uuid: string) => void;
  onNoteChange?: (uuid: string, note: string) => void;
  // Claims whose first approve came back held (owner recently worked the customer).
  heldClaims?: Set<string>;
}

const ClaimsTable: React.FC<Props> = ({
  claims, mode, isSuperAdmin,
  actionLoading, adminNotes = {}, onApprove, onDecline, onNoteChange, heldClaims,
}) => {
  if (claims.length === 0) {
    return (
      <div className="text-center py-20">
        <ClipboardList className="h-12 w-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">No claims found</p>
        <p className="text-sm text-gray-400 mt-1">
          {mode === 'mine'
            ? 'Use the search bar on a lead to submit a claim.'
            : mode === 'pending'
            ? 'No pending requests at the moment.'
            : 'No claims match the selected filter.'}
        </p>
      </div>
    );
  }

  const showClaimedBy = mode !== 'mine';
  const showAction = isSuperAdmin && mode === 'pending';
  const showActioned = mode !== 'pending';

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <th className="px-4 py-3 text-left">Lead</th>
            {showClaimedBy && <th className="px-4 py-3 text-left">Claimed By</th>}
            <th className="px-4 py-3 text-left">Previous Owner</th>
            <th className="px-4 py-3 text-left">New Owner</th>
            <th className="px-4 py-3 text-left">Disposition</th>
            <th className="px-4 py-3 text-left">Reason</th>
            <th className="px-4 py-3 text-left">Requested</th>
            <th className="px-4 py-3 text-left">Status</th>
            {showActioned && <th className="px-4 py-3 text-left">Approved By</th>}
            {showActioned && <th className="px-4 py-3 text-left">Approved Time</th>}
            {showAction && <th className="px-4 py-3 text-left">Action</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {claims.map(claim => {
            const cfg = statusConfig[claim.status];
            const StatusIcon = cfg.icon;
            return (
              <tr key={claim.uuid} className="hover:bg-gray-50 transition-colors">
                {/* Lead */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div>
                      <span className="font-medium text-gray-900">{claim.lead_name || claim.lead_phone || 'Unknown'}</span>
                      {claim.lead_phone && claim.lead_name && (
                        <p className="text-xs text-gray-400">{claim.lead_phone}</p>
                      )}
                    </div>
                    {isSuperAdmin && (
                      <button
                        onClick={() => window.open(`/lead/${claim.lead_uuid}`, '_blank')}
                        className="text-brandColor hover:opacity-75 transition-opacity shrink-0"
                        title="Open lead"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>

                {showClaimedBy && (
                  <td className="px-4 py-3 text-gray-700">{claim.claimed_by_user_name || '—'}</td>
                )}

                <td className="px-4 py-3 text-gray-700">{claim.current_owner_name || '—'}</td>

                <td className="px-4 py-3 text-gray-700">
                  {claim.status === 'approved'
                    ? (claim.claimed_by_user_name || '—')
                    : <span className="text-gray-300">—</span>}
                </td>

                {/* Disposition */}
                <td className="px-4 py-3">
                  {(claim.disposition_l1_label || claim.disposition_l2_label || claim.disposition_l3_label) ? (
                    <div className="flex flex-col gap-0.5">
                      {claim.disposition_l1_label && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded border border-purple-100 w-fit">{claim.disposition_l1_label}</span>
                      )}
                      {claim.disposition_l2_label && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100 w-fit">{claim.disposition_l2_label}</span>
                      )}
                      {claim.disposition_l3_label && (
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded border border-green-100 w-fit">{claim.disposition_l3_label}</span>
                      )}
                    </div>
                  ) : <span className="text-gray-300">—</span>}
                </td>

                {/* Reason */}
                <td className="px-4 py-3 max-w-[180px]">
                  {claim.reason
                    ? <span className="text-gray-600 italic text-xs line-clamp-2">"{claim.reason}"</span>
                    : <span className="text-gray-300">—</span>}
                  {claim.admin_note && (
                    <p className="text-xs text-gray-400 mt-0.5 not-italic">Note: {claim.admin_note}</p>
                  )}
                </td>

                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{safeDate(claim.created_at)}</td>

                {/* Status badge */}
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                    <StatusIcon className="h-3 w-3" />
                    {cfg.label}
                  </span>
                </td>

                {showActioned && (
                  <td className="px-4 py-3 text-gray-700">{claim.actioned_by_user_name || '—'}</td>
                )}
                {showActioned && (
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{safeDate(claim.actioned_at)}</td>
                )}

                {/* Approve / Decline actions */}
                {showAction && (
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5 min-w-[200px]">
                      <input
                        type="text"
                        value={adminNotes[claim.uuid] || ''}
                        onChange={e => onNoteChange?.(claim.uuid, e.target.value)}
                        placeholder="Note (optional)..."
                        className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brandColor"
                      />
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => onApprove?.(claim.uuid)}
                          disabled={!!actionLoading}
                          className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {actionLoading === claim.uuid + '_approve'
                            ? <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <CheckCircle className="h-3 w-3" />}
                          {heldClaims?.has(claim.uuid) ? 'Approve anyway' : 'Approve'}
                        </button>
                        <button
                          onClick={() => onDecline?.(claim.uuid)}
                          disabled={!!actionLoading}
                          className="flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {actionLoading === claim.uuid + '_decline'
                            ? <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <XCircle className="h-3 w-3" />}
                          Decline
                        </button>
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ClaimsTable;
